import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import {
  fetchAvailableScheduleDates,
  saveRoomSchedule,
} from "../api/bedroomAllocationApi";
import DraggableDialogPaper from "./DraggableDialogPaper";


function RoomScheduleDialog({ open, room, year, onClose, onSave }) {
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(null);
  const initialScheduledDates = useMemo(
    () =>
      Array.isArray(room?.scheduledDates)
        ? [...room.scheduledDates].map(String).sort()
        : [],
    [room]
  );

  const currentScheduledDates = useMemo(
    () => Array.from(selectedDates).sort(),
    [selectedDates]
  );

  const hasChanges = useMemo(() => {
    if (initialScheduledDates.length !== currentScheduledDates.length) return true;

    return initialScheduledDates.some((date, index) => date !== currentScheduledDates[index]);
  }, [initialScheduledDates, currentScheduledDates]);

  useEffect(() => {
    if (!open || !room) return;

    const initialDates = Array.isArray(room.scheduledDates)
      ? room.scheduledDates.map(String)
      : [];

    setSelectedDates(new Set(initialDates));

    if (initialDates.length > 0) {
      setCalendarMonth(dayjs(initialDates[0]));
    } else {
      setCalendarMonth(dayjs(`${year}-01-01`));
    }
  }, [open, room, year]);

  useEffect(() => {
    if (!open || !room) return;

    const load = async () => {
      setLoading(true);
      try {
        const dates = await fetchAvailableScheduleDates(year, room.campus);
        setAvailableDates(Array.isArray(dates) ? dates : []);

        if ((!room.scheduledDates || room.scheduledDates.length === 0) && dates?.length > 0) {
          setCalendarMonth(dayjs(dates[0]));
        }
      } catch (err) {
        console.error("Failed to load available schedule dates", err);
        setAvailableDates([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, room, year]);

  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);

  const toggleDate = (date) => {
    const key = dayjs(date).format("YYYY-MM-DD");

    if (!availableSet.has(key)) return;

    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isSelected = (date) => {
    const key = dayjs(date).format("YYYY-MM-DD");
    return selectedDates.has(key);
  };

  const handleSave = async () => {
    const dates = Array.from(selectedDates).sort();

    await saveRoomSchedule(room.roomId, year, dates);

    onSave?.(room.roomId, dates);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperComponent={DraggableDialogPaper}
    >
      <DialogTitle id="draggable-dialog-title" sx={{ cursor: "move" }}>
        Schedule – {room.roomName}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select the dates this room is available.
            </Typography>

            <DateCalendar
              value={calendarMonth}
              onMonthChange={(newMonth) => setCalendarMonth(dayjs(newMonth))}
              onChange={(date) => {
                if (date) {
                  toggleDate(date);
                  setCalendarMonth(dayjs(date));
                }
              }}
              shouldDisableDate={(date) => {
                const key = dayjs(date).format("YYYY-MM-DD");
                return !availableSet.has(key);
              }}
              slots={{
                day: (props) => {
                  const selected = isSelected(props.day);

                  return (
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        lineHeight: "36px",
                        textAlign: "center",
                        borderRadius: "50%",
                        cursor: props.disabled ? "default" : "pointer",
                        bgcolor: selected ? "primary.main" : undefined,
                        color: selected ? "white" : undefined,
                        opacity: props.disabled ? 0.3 : 1,
                      }}
                      onClick={() => !props.disabled && toggleDate(props.day)}
                    >
                      {props.day.date()}
                    </Box>
                  );
                },
              }}
            />

            <Typography variant="body2" sx={{ mt: 2 }}>
              {selectedDates.size} selected day{selectedDates.size === 1 ? "" : "s"}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!hasChanges}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoomScheduleDialog;